/* -------------------------------------------------------------------------- */
/*                              Accordion styles                              */
/* -------------------------------------------------------------------------- */

@import './bq-accordion.variables';

:host {
  @apply block;
}

.bq-accordion:not(.no-animation) {
  /* -------------------------------------------------------------------- */
  /* Opt-in the component to animating to/from intrinsic sizing keywords  */
  /* Details: https://chrome.dev/css-wrapped-2024/#animate-to-height-auto */
  /* -------------------------------------------------------------------- */
  @apply supports-[interpolate-size:_allow-keywords]:[interpolate-size:_allow-keywords];
}

.bq-accordion {
  &.disabled {
    @apply cursor-not-allowed opacity-60;

    .bq-accordion__header {
      @apply pointer-events-none;
    }
  }

  &.small .bq-accordion__header {
    @apply gap-[--bq-accordion--small-gap] pe-[--bq-accordion--small-padding-end] ps-[--bq-accordion--small-padding-start] p-b-[--bq-accordion--small-padding-y];
    @apply rounded-ee-[--bq-accordion--small-radius] rounded-es-[--bq-accordion--small-radius] rounded-se-[--bq-accordion--small-radius] rounded-ss-[--bq-accordion--small-radius];
  }

  &.medium .bq-accordion__header {
    @apply gap-[--bq-accordion--medium-gap] pe-[--bq-accordion--medium-padding-end] ps-[--bq-accordion--medium-padding-start] p-b-[--bq-accordion--medium-padding-y];
    @apply rounded-ee-[--bq-accordion--medium-radius] rounded-es-[--bq-accordion--medium-radius] rounded-se-[--bq-accordion--medium-radius] rounded-ss-[--bq-accordion--medium-radius];
  }

  &[open] .bq-accordion__header {
    @apply border-[length:--bq-accordion--expanded-border-width] border-[color:--bq-accordion--expanded-border-color];
    @apply rounded-ee-[0px] rounded-es-[0px];

    border-style: var(--bq-accordion--expanded-border-style);
  }

  &.small .bq-accordion__content {
    @apply rounded-ee-[--bq-accordion--small-radius] rounded-es-[--bq-accordion--small-radius] rounded-se-[0px] rounded-ss-[0px];
  }

  &.medium .bq-accordion__content {
    @apply rounded-ee-[--bq-accordion--medium-radius] rounded-es-[--bq-accordion--medium-radius] rounded-se-[0px] rounded-ss-[0px];
  }

  &.filled {
    .bq-accordion__header {
      @apply bg-[--bq-accordion--filled-collapsed-background] hover:bg-hover-ui-secondary;
    }

    .bq-accordion__header--text {
      @apply text-[--bq-accordion--filled-collapsed-text-color];
    }

    .bq-accordion__content {
      @apply border-[length:--bq-accordion--panel-filled-border-width] border-[color:--bq-accordion--panel-filled-border-color];

      border-style: var(--bq-accordion--panel-filled-border-style);
    }

    &[open] .bq-accordion__header {
      @apply bg-[--bq-accordion--filled-expanded-background] hover:bg-hover-ui-secondary;
    }

    &[open] .bq-accordion__header--text {
      @apply text-[--bq-accordion--filled-expanded-text-color];
    }
  }

  &.ghost {
    .bq-accordion__header {
      @apply bg-[--bq-accordion--ghost-collapsed-background] hover:bg-hover-ui-secondary;
    }

    .bq-accordion__header--text {
      @apply text-[--bq-accordion--ghost-collapsed-text-color];
    }

    .bq-accordion__content {
      @apply border-[length:--bq-accordion--panel-ghost-border-width] border-[color:--bq-accordion--panel-ghost-border-color];

      border-style: var(--bq-accordion--panel-ghost-border-style);
    }

    &[open] .bq-accordion__header {
      @apply bg-[--bq-accordion--ghost-expanded-background] hover:bg-hover-ui-secondary;
    }

    &[open] .bq-accordion__header--text {
      @apply text-[--bq-accordion--ghost-expanded-text-color];
    }
  }

  &.small.filled .bq-accordion__content {
    @apply pe-[--bq-accordion--panel-small-filled-padding-end] ps-[--bq-accordion--panel-small-filled-padding-start] p-b-[--bq-accordion--panel-small-filled-padding-y];
  }

  &.medium.filled .bq-accordion__content {
    @apply pe-[--bq-accordion--panel-medium-filled-padding-end] ps-[--bq-accordion--panel-medium-filled-padding-start] p-b-[--bq-accordion--panel-medium-filled-padding-y];
  }

  &.small.ghost .bq-accordion__content {
    @apply pe-[--bq-accordion--panel-small-ghost-padding-end] ps-[--bq-accordion--panel-small-ghost-padding-start] p-b-[--bq-accordion--panel-small-ghost-padding-y];
  }

  &.medium.ghost .bq-accordion__content {
    @apply pe-[--bq-accordion--panel-medium-ghost-padding-end] ps-[--bq-accordion--panel-medium-ghost-padding-start] p-b-[--bq-accordion--panel-medium-ghost-padding-y];
  }
}

.bq-accordion::details-content {
  @apply block overflow-clip transition-all duration-300 ease-in-out bs-0 [transition-behavior:allow-discrete];
}

.bq-accordion[open]::details-content {
  @apply bs-auto;
}

.bq-accordion__header {
  // Since there's an overflow on the <summary> element, the focus outline is not visible,
  // so we force it to be inset to avoid the overflow hidden.
  --bq-ring-offset-width: -2px;

  @apply flex cursor-pointer select-none list-none items-center transition-colors duration-300 ease-in-out focus-visible:focus;
  @apply border-[length:--bq-accordion--collapsed-border-width] border-[color:--bq-accordion--collapsed-border-color];

  border-style: var(--bq-accordion--collapsed-border-style);

  &::marker,
  &::-webkit-details-marker {
    @apply hidden;
  }
}

.bq-accordion__header--text {
  @apply flex-1;
}

.bq-accordion__header--prefix,
.bq-accordion__header--suffix {
  @apply flex items-center justify-center;
}
